import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, tap } from 'rxjs';

import { HealthChecker, HealthIssueDetails } from '@ideals/services/health-checker';
import { APP_CONFIG, NAVIGATOR } from '@ideals/types';

interface ErrorReport {
  readonly details: {
    readonly domain: string;
  };
  readonly error: unknown;
  readonly message: string;
  readonly status: number;
  readonly statusText: string;
  readonly url: string;
}

export class ServersChecker extends HealthChecker {
  readonly #httpClient = new HttpClient(inject(HttpBackend));
  readonly #navigator = inject(NAVIGATOR);
  readonly #urls = inject(APP_CONFIG).healthCheckUrls;

  #errorReports: ErrorReport[] = [];

  getIssueDetails(): HealthIssueDetails {
    const domains = this.#errorReports.map((report) => report.details.domain);

    return {
      cta: 'help_modal.TEXT.Ask_your_IT',
      description: 'help_modal.TEXT.Your_network_is_blocking',
      details: domains.join(';\n '),
      title: 'help_modal.TEXT.Server_check_failed',
    };
  }

  getReport(): { [key: string]: unknown; } {
    return { servers: this.#errorReports };
  }

  hasIssue(): Observable<boolean> {
    this.#errorReports = [];

    const errors$ = this.#urls.map((url) => {
      return this.#httpClient.get(url, { responseType: 'text' }).pipe(
        map(() => null),
        catchError((error: HttpErrorResponse) => of(error)),
      );
    });

    return forkJoin(errors$).pipe(
      map((errors: (HttpErrorResponse | null)[]) => errors.filter(Boolean)),
      tap((errors) => {
        this.#errorReports = errors.map((error) => this.#createReport(error));
      }),
      map((errors) => errors.length > 0),
    );
  }

  #createReport(error: HttpErrorResponse): ErrorReport {
    let message = error.message;
    let statusText = error.statusText;
    const domain = error.url ? new URL(error.url).hostname : '';

    if (error.status === 0) {
      message = this.#navigator.onLine
        ? 'Network/server error: request not completed (possible CORS/Firewall/DNS/Timeout)'
        : 'No internet connection';
      statusText = 'Connection failed';
    }

    return {
      details: { domain },
      error: error.error,
      message,
      status: error.status,
      statusText,
      url: error.url ?? '',
    };
  }
}
